import {
  Checkbox,
  MenuItem,
  Select,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { isFieldHidden } from '../../../utils/fieldUtil'
import {
  useAllValues,
  useFieldCurrencyMap,
  useProcessContext,
  useSelectedTableData,
} from '../../../../../store'
import {
  ACTIVITY_TYPES,
  CURRENCY_COUNTRY_CODE,
  FIELD_TYPES,
} from '../../../../../utility/constants'
import { getColumnFilterId, getSelectableTableId } from '../../../utils/utils'
import { useAuthContext } from '../../../../../hooks/useAuthContext'
import { currencyFieldService } from '../../../../../services/currencyFieldService'
import { computeCurrency } from '../../../utils/currency'
import { autoComputeFieldValue } from '../../../utils/autoCompute'
import { getColumnWidth } from '../../../../../utility/columnWidth'

const TableHeading = ({
  columns,
  width,
  hiddenColumnIds = [],
  style,
  filterValues,
  setFilterValues,
  filterEnabled,
  fields,
  isSelectable = false,
  field,
  freezeColumnId,
  rows,
}) => {
  const { user } = useAuthContext()
  const allValues = useAllValues((state) => state.allValues)
  const setAllValues = useAllValues((state) => state.setAllValues)
  const processContext = useProcessContext((state) => state.processContext)
  const activityType = processContext?.activityTemplate?.activity_type
  const version = processContext?.processData?.version
  const conditionalEnableDisable = processContext?.conditionalEnableDisable
  const defaultCurrency = processContext?.companyDetails?.default_currency_code
  const searchTimerRef = useRef()
  const fieldCurrencyMap = useFieldCurrencyMap((state) => state.fieldCurrencyMap)
  const setFieldCurrencyMap = useFieldCurrencyMap((state) => state.setFieldCurrencyMap)
  const processId = processContext?.processData?.id
  const updateSelectedTableRow = useSelectedTableData((state) => state.updateSelectedTableRow)
  const selectedTableData = useSelectedTableData((state) => state.selectedTableData)
  const actionCellRef = useRef(null)
  const serialNoRef = useRef(null)
  const [actionWidth, setActionWidth] = useState(0)
  const [serialNoWidth, setSerialNoWidth] = useState(0)
  const listDataMetadata = useProcessContext((state) => state.processContext.listDataMetadata)

  useEffect(() => {
    if (actionCellRef.current) {
      setActionWidth(actionCellRef.current.offsetWidth)
    }
    if (serialNoRef.current) {
      setSerialNoWidth(serialNoRef.current.offsetWidth)
    }
  }, [freezeColumnId])

  const isReviewSwitchHidden = (field) => {
    return (activityType == ACTIVITY_TYPES.DATA_ENTRY && version == 1) || field.is_read_only
  }

  const triggerAutoCompute = async (allValues, listDataMetadata, processContext) => {
    const computedFields = await autoComputeFieldValue(
      processContext?.allFields,
      allValues,
      listDataMetadata,
      {
        user: user,
        processPrefix: processContext?.activityTemplate?.prefix,
        processReferenceNum: processContext?.processData?.process_reference_num,
        processTemplateId: processContext?.processData?.process_template_id,
        selectedTableData: selectedTableData,
        tableSubfieldsMap: processContext?.tableSubfieldsMap,
        version: processContext?.processData?.version,
        allProcessData: processContext?.allProcessData,
      }
    )
    return computedFields
  }

  const handleAllRowSelected = useCallback(
    async (rows, isSelected) => {
      const rowStrings = rows.map((row) => String(row.row))

      if (isSelected) {
        await updateSelectedTableRow(field.id, rowStrings)
      } else {
        await updateSelectedTableRow(field.id, [])
      }

      const computedValues = await triggerAutoCompute(allValues, listDataMetadata, processContext)
      setAllValues({ ...allValues, ...computedValues })
    },
    [selectedTableData, columns]
  )
  // Checkbox UI logic
  const renderActions = () => {
    const isAllSelected =
      selectedTableData[getSelectableTableId(field.id)] &&
      selectedTableData[getSelectableTableId(field.id)].length === rows.length

    return (
      <TableCell
        ref={actionCellRef}
        sx={{
          position: 'sticky',
          left: 0,
          backgroundColor: 'white',
          minWidth: isSelectable ? 84 : 46,
          top: style.top,
        }}
      >
        {isSelectable ? (
          <Checkbox
            sx={{
              position: 'sticky',
              left: 20,
              zIndex: 3,
              backgroundColor: 'white',
              top: 0,
            }}
            color="primary"
            size="small"
            onChange={(e) => handleAllRowSelected(rows, e.target.checked)}
            disabled={activityType != ACTIVITY_TYPES.DATA_ENTRY}
            checked={isAllSelected}
            title={isAllSelected ? 'Unselect all' : 'Select all'}
          />
        ) : null}
      </TableCell>
    )
  }

  const renderSerialNo = () => {
    return (
      <TableCell
        ref={serialNoRef}
        sx={{
          minWidth: 60,
          position: 'sticky',
          zIndex: 3,
          backgroundColor: 'white',
          top: 0,
          left: actionWidth,
        }}
      >
        Sl. No.
      </TableCell>
    )
  }

  const onSearchTextChange = (e, field) => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }
    searchTimerRef.current = setTimeout(() => {
      if (e.target.value == '') {
        let oldData = { ...filterValues }
        delete oldData[getColumnFilterId(field.id)]
        setFilterValues(oldData)
      } else {
        setFilterValues({
          ...filterValues,
          [getColumnFilterId(field.id)]: e.target.value,
        })
      }
      searchTimerRef.current = null
    }, 1000)
  }

  const isHidden = (it) => {
    return !isFieldHidden(it, processContext?.hiddenFields, hiddenColumnIds, {
      allValues,
      conditionalEnableDisable,
    })
  }

  const getSelectedCurrency = (fieldId) => {
    const currency = fieldCurrencyMap[fieldId]
      ? Object.entries(CURRENCY_COUNTRY_CODE).find(
          ([key, value]) => value === fieldCurrencyMap[fieldId]
        )?.[0]
      : defaultCurrency

    return currency
  }

  const deleteNonDefaultCurrency = async (id) => {
    try {
      await currencyFieldService.deleteNonDefaultCurrency(user, processId, id)
    } catch (error) {
      console.error('Error deleting non-default currency:', error)
      throw error // Rethrow the error to propagate it up
    }
  }

  const saveNonDefaultCurrencyToDb = async (newValueObj) => {
    // save current changes

    await currencyFieldService.addNonDefaultCurrency(user, {
      field_template_id: newValueObj?.id,
      currency_code: Object.keys(CURRENCY_COUNTRY_CODE).find(
        (key) => CURRENCY_COUNTRY_CODE[key] === newValueObj?.currency
      ),
      process_id: processContext?.processData?.id,
    })

    const { updatedFieldCurrencyMap, nonDefaultValues } = computeCurrency(
      { ...fieldCurrencyMap, [newValueObj?.id]: newValueObj?.currency },
      fields,
      defaultCurrency,
      allValues
    )

    for (const { id, currency } of nonDefaultValues) {
      if (CURRENCY_COUNTRY_CODE[defaultCurrency] == currency) {
        await deleteNonDefaultCurrency(id)
      } else {
        await currencyFieldService.addNonDefaultCurrency(user, {
          field_template_id: id,
          currency_code: Object.entries(CURRENCY_COUNTRY_CODE).find(
            ([_, value]) => value == currency
          )[0],
          process_id: processContext?.processData?.id,
        })
      }
    }

    let map = {
      ...updatedFieldCurrencyMap,
      ...nonDefaultValues.reduce((acc, cur) => {
        return { ...acc, [cur.id]: cur.currency }
      }, {}),
    }

    setFieldCurrencyMap(map)
  }

  const calculateOffset = (index) => {
    const staticPartWidth = actionWidth + serialNoWidth

    const visibleColumns = columns.filter((fld) => !hiddenColumnIds?.includes(fld.id))

    if (index <= 0) {
      return staticPartWidth
    } else {
      const dynamicPartWidth =
        staticPartWidth +
        visibleColumns
          .slice(0, index)
          .reduce((acc, cur) => acc + getColumnWidth(cur, isReviewSwitchHidden), 0)
      return dynamicPartWidth
    }
  }

  return (
    <>
      {' '}
      <TableHead
        style={{
          ...style,
          backgroundColor: 'white',
          zIndex: 1,
          width: width,
          boxSizing: 'border-box',
          padding: 0,
        }}
      >
        <TableRow style={{ width: width }}>
          {(activityType == ACTIVITY_TYPES.DATA_ENTRY ||
            activityType === ACTIVITY_TYPES.LONG_RUNNING) &&
            renderActions()}
          {renderSerialNo()}

          {columns
            .filter((it) => !hiddenColumnIds?.includes(it.id)) // Exclude hidden columns
            .map((it, index) => {
              const visibleColumns = columns.filter((col) => !hiddenColumnIds?.includes(col.id))

              const freezeIndex = visibleColumns.findIndex((col) =>
                freezeColumnId?.includes(col.id)
              )
              const isFrozen = visibleColumns.indexOf(it) <= freezeIndex

              const leftOffset = calculateOffset(visibleColumns.indexOf(it))

              return (
                isHidden(it) && (
                  <TableCell
                    className={`f_${it.id}`}
                    key={it.id}
                    style={{
                      backgroundColor: isFrozen ? 'lightgrey' : 'white',
                      width: width,
                      minWidth: getColumnWidth(it, isReviewSwitchHidden) + 'px',
                      padding: '6px 10px',
                      position: isFrozen ? 'sticky' : 'static',
                      left: isFrozen ? `${leftOffset}px` : 'auto',
                      zIndex: isFrozen ? 1 : 'auto',
                      top: 0,
                    }}
                  >
                    <div key={it.id}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        {it.field_template_name} {it.is_required ? '*' : ''}
                      </Typography>
                      {it.data_structure == FIELD_TYPES.CURRENCY &&
                        !it.is_computed &&
                        !it.is_read_only &&
                        activityType != ACTIVITY_TYPES.REVIEW && (
                          <Select
                            value={getSelectedCurrency(it.id)}
                            onChange={async (e) =>
                              await saveNonDefaultCurrencyToDb({
                                id: it.id,
                                currency: CURRENCY_COUNTRY_CODE[e.target.value],
                              })
                            }
                            variant="outlined"
                            size="small"
                            style={{ marginRight: '8px' }}
                          >
                            {Object.keys(CURRENCY_COUNTRY_CODE).map((currency) => (
                              <MenuItem key={currency} value={currency}>
                                {CURRENCY_COUNTRY_CODE[currency]}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                    </div>
                  </TableCell>
                )
              )
            })}
        </TableRow>
        {filterEnabled && (
          <TableRow style={{ width: width }}>
            {(activityType == ACTIVITY_TYPES.DATA_ENTRY ||
              activityType === ACTIVITY_TYPES.LONG_RUNNING) && (
              <TableCell sx={{ minWidth: 46 }}></TableCell>
            )}
            <TableCell sx={{ minWidth: 46 }}></TableCell>
            {columns.map((it) => {
              return (
                isHidden(it) && (
                  <TableCell
                    key={it.id}
                    style={{
                      backgroundColor: 'white',
                      width: width,
                      minWidth: getColumnWidth(it, isReviewSwitchHidden) + 'px',
                      padding: '6px 10px',
                    }}
                  >
                    <TextField
                      variant="standard"
                      size="small"
                      placeholder="Search here"
                      onChange={(e) => onSearchTextChange(e, it)}
                    />
                  </TableCell>
                )
              )
            })}
          </TableRow>
        )}
      </TableHead>
    </>
  )
}

export default TableHeading
